import React, { useEffect, useRef } from "react";
import { useThree } from "@react-three/fiber";
import * as THREE from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader";
import { useWatch } from "react-hook-form";
import { formConfig } from "../form/formConfig";
import { materialConfig } from "./materials/materialConfig";
import { TextureManager } from "../utils/TextureManager";
import { loadMaterialTextures as loadKTX2MaterialTextures } from "../utils/KTX2TextureLoader";
import { getCachedMaterial, setCachedMaterial, isMaterialCached } from "../utils/materialCache";

export const BackendModel = ({ objData }) => {
  const { scene, camera, controls, gl } = useThree();
  const currentObjRef = useRef(null);
  const formValues = useWatch();
  const textureManagerRef = useRef(new TextureManager()); // Texture manager instance
  const hasInitializedCamera = useRef(false); // Track if camera has been set once
  const isLoadingNewModelRef = useRef(false); // Track if we're loading a new model to prevent double render

  // Feature flag: Enable/disable TextureManager
  // Set to false if you want to use legacy texture scaling
  const USE_TEXTURE_MANAGER = true;

  // Initialize TextureManager with WebGL renderer for KTX2 support
  useEffect(() => {
    if (gl && textureManagerRef.current) {
      textureManagerRef.current.setRenderer(gl);
      console.log('✅ TextureManager initialized with KTX2 support');
    }
  }, [gl]);

  console.log('🔨 BackendModel component rendered');
  console.log('📋 Form values:', formValues);

  const matchesMeshSelector = (meshName, selector) => {
    if (!selector) return false;

    // Support both single value and array of values
    const values = Array.isArray(selector.value) ? selector.value : [selector.value];

    // Check if meshName matches any of the values
    return values.some(value => {
      switch (selector.type) {
        case 'prefix':
          return meshName.startsWith(value);
        case 'exact':
          return meshName === value;
        case 'contains':
          return meshName.includes(value);
        case 'regex':
          return new RegExp(value).test(meshName);
        default:
          return false;
      }
    });
  };

  const isExcluded = (meshName, excluder) => {
    if (!excluder) return false;

    // Support both single value and array of values
    const values = Array.isArray(excluder.value) ? excluder.value : [excluder.value];

    // Check if meshName matches any exclusion rule
    return values.some(value => {
      switch (excluder.type) {
        case 'prefix':
          return meshName.startsWith(value);
        case 'exact':
          return meshName === value;
        case 'contains':
          return meshName.includes(value);
        case 'regex':
          return new RegExp(value).test(meshName);
        default:
          return false;
      }
    });
  };

  const applyFormColors = (obj) => {
    if (!obj || !formValues) return;

    console.log('🎨 Step 3: Applying colors where colorPicker matches...');
    let colorCount = 0;

    obj.traverse((child) => {
      if (child.isMesh && child.material) {
        // Find which formConfig entry matches this mesh
        for (const config of formConfig) {
          if (config.type === 'colorPicker' && config.meshSelector) {
            // Check if mesh is excluded
            if (config.meshExcluder && isExcluded(child.name, config.meshExcluder)) {
              console.log(`  ⊘ ${child.name} → excluded by meshExcluder`);
              continue; // Skip this config
            }

            if (matchesMeshSelector(child.name, config.meshSelector)) {
              const selectedColorCode = formValues[config.name];
              const colorOption = config.options?.find(opt => opt.code === selectedColorCode);

              if (colorOption) {
                // Check if mesh already has a texture map (from materialPicker)
                if (child.material.map) {
                  // If it has a texture, don't override the color - keep it white
                  console.log(`  ○ ${child.name} → has texture, keeping white color (ignoring colorPicker)`);
                } else {
                  // No texture, apply the color
                  child.material.color.set(colorOption.color);
                  child.material.needsUpdate = true;
                  colorCount++;
                  console.log(`  ✓ ${child.name} → ${colorOption.label} (visible)`);
                }
                child.visible = true; // Make visible on match regardless
              }
              break; // Stop after first match
            }
          }
        }
      }
    });

    console.log(`🎨 Applied ${colorCount} colors and made visible`);
  };

  const loadMaterialTextures = async (materialKey) => {
    // Check shared cache first
    if (isMaterialCached(materialKey)) {
      console.log(`♻️ [BackendModel] Using cached material for ${materialConfig[materialKey]?.name}`);
      return getCachedMaterial(materialKey);
    }

    const matConfig = materialConfig[materialKey];

    if (!matConfig) {
      console.error(`Material ${materialKey} not found in materialConfig`);
      return null;
    }

    console.log(`📦 Loading KTX2 textures for ${matConfig.name}...`);

    // Load textures using KTX2 with fallback to PNG/JPG
    const textures = await loadKTX2MaterialTextures(matConfig, gl, true);

    // Configure texture scaling
    const totalTextures = Object.keys(textures).length;
    Object.values(textures).forEach((texture) => {
      if (texture) {
        texture.repeat.set(matConfig.textureScale, matConfig.textureScale);
      }
    });

    console.log(`  📦 All textures loaded (${totalTextures}) for ${matConfig.name}`);

    // Trigger material update
    if (currentObjRef.current) {
      console.log(`  ✅ All textures loaded for ${matConfig.name}, updating materials...`);
      currentObjRef.current.traverse((child) => {
        if (child.isMesh && child.material && child.material.map === textures.albedo) {
          child.material.needsUpdate = true;
        }
      });
    }

    const materialData = { textures, properties: matConfig.properties };

    // Store in shared cache
    setCachedMaterial(materialKey, materialData);

    return materialData;
  };

  const createMaterialFromConfig = (textures, properties, skipTextures = false) => {
    const materialOptions = {
      color: 0xffffff,
      roughness: properties.roughness,
      metalness: properties.metalness,
      flatShading: false,
    };

    // Only add textures if skipTextures is false
    if (!skipTextures) {
      if (textures.albedo) {
        materialOptions.map = textures.albedo;
      }
      if (textures.normal) {
        materialOptions.normalMap = textures.normal;
        materialOptions.normalScale = new THREE.Vector2(...properties.normalScale);
      }
      if (textures.ao) {
        materialOptions.aoMap = textures.ao;
        materialOptions.aoMapIntensity = properties.aoMapIntensity;
      }
      if (textures.height) {
        materialOptions.displacementMap = textures.height;
        materialOptions.displacementScale = properties.displacementScale;
      }
      if (textures.roughness) {
        materialOptions.roughnessMap = textures.roughness;
      }
    }

    materialOptions.envMapIntensity = properties.envMapIntensity;

    return new THREE.MeshStandardMaterial(materialOptions);
  };

  const ensureGeometrySetup = (geometry, meshName = 'unknown') => {
    // Compute normals if they don't exist
    if (!geometry.attributes.normal) {
      geometry.computeVertexNormals();
      console.log(`  🔧 [${meshName}] Generated vertex normals`);
    }

    // ALWAYS regenerate UVs for consistent box projection
    // (OBJ file UVs may be incorrect for texture tiling)
    const hasExistingUVs = !!geometry.attributes.uv;
    if (hasExistingUVs) {
      console.log(`  🔧 [${meshName}] Existing UVs found, will regenerate for proper tiling...`);
    } else {
      console.log(`  🔧 [${meshName}] No UVs found, generating box projection UVs...`);
    }

    const positions = geometry.attributes.position;
    const normals = geometry.attributes.normal;
    const uvs = new Float32Array(positions.count * 2);

      // Calculate bounding box for normalized UVs
      geometry.computeBoundingBox();
      const bbox = geometry.boundingBox;
      const sizeX = bbox.max.x - bbox.min.x;
      const sizeY = bbox.max.y - bbox.min.y;
      const sizeZ = bbox.max.z - bbox.min.z;

      console.log(`    📏 Geometry size: ${sizeX.toFixed(2)} × ${sizeY.toFixed(2)} × ${sizeZ.toFixed(2)}`);

      // Determine longest axis for wood grain orientation IN LOCAL SPACE
      let longestAxis = 'x';
      let longestSize = sizeX;
      if (sizeY > longestSize) {
        longestAxis = 'y';
        longestSize = sizeY;
      }
      if (sizeZ > longestSize) {
        longestAxis = 'z';
        longestSize = sizeZ;
      }

      console.log(`    🧭 Longest axis: ${longestAxis.toUpperCase()} (${longestSize.toFixed(0)}mm)`);

      // IMPORTANT: We'll use world-space UVs (in mm) instead of normalized (0-1)
      // This way, texture.repeat can be set to 1 and tiling happens automatically
      // based on the actual geometry size
      const uvScale = 1.0; // UVs in world space (mm)

      for (let i = 0; i < positions.count; i++) {
        const x = positions.getX(i);
        const y = positions.getY(i);
        const z = positions.getZ(i);

        const nx = normals ? Math.abs(normals.getX(i)) : 0;
        const ny = normals ? Math.abs(normals.getY(i)) : 0;
        const nz = normals ? Math.abs(normals.getZ(i)) : 0;

        let u, v;

        // Use box projection based on normal
        // CRITICAL: Orient UVs so grain always runs along longest dimension
        // UVs are in world space (mm), not normalized
        if (nx > ny && nx > nz) {
          // X-aligned face (looking at YZ plane)
          // We see Y and Z axes
          if (longestAxis === 'y') {
            // Y is longest, map Y to U (horizontal)
            u = (y - bbox.min.y) * uvScale;
            v = (z - bbox.min.z) * uvScale;
          } else {
            // Z is longest, map Z to U (horizontal)
            u = (z - bbox.min.z) * uvScale;
            v = (y - bbox.min.y) * uvScale;
          }
        } else if (ny > nx && ny > nz) {
          // Y-aligned face (looking at XZ plane) - top/bottom
          // We see X and Z axes
          if (longestAxis === 'x') {
            // X is longest, map X to U (horizontal)
            u = (x - bbox.min.x) * uvScale;
            v = (z - bbox.min.z) * uvScale;
          } else {
            // Z is longest, map Z to U (horizontal)
            u = (z - bbox.min.z) * uvScale;
            v = (x - bbox.min.x) * uvScale;
          }
        } else {
          // Z-aligned face (looking at XY plane) - front/back
          // We see X and Y axes
          if (longestAxis === 'x') {
            // X is longest, map X to U (horizontal)
            u = (x - bbox.min.x) * uvScale;
            v = (y - bbox.min.y) * uvScale;
          } else {
            // Y is longest, map Y to U (horizontal)
            u = (y - bbox.min.y) * uvScale;
            v = (x - bbox.min.x) * uvScale;
          }
        }

        uvs[i * 2] = u;
        uvs[i * 2 + 1] = v;
      }

    geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
    console.log(`    ✅ [${meshName}] Generated ${positions.count} UV coordinates`);

    if (!geometry.attributes.uv2) {
      geometry.setAttribute('uv2', geometry.attributes.uv);
    }
  };

  const applyMaterialToObject = (obj, materialData) => {
    if (!materialData) return;

    const { textures, properties } = materialData;

    obj.traverse((child) => {
      if (child.isMesh) {
        ensureGeometrySetup(child.geometry, child.name);
        child.material = createMaterialFromConfig(textures, properties);
        child.castShadow = true;
        child.receiveShadow = true;
      }
    });
  };

  const applyMaterialsFromForm = async (obj) => {
    if (!obj || !formValues) {
      console.warn('⚠️ Cannot apply materials: obj or formValues missing', { obj: !!obj, formValues: !!formValues });
      return;
    }

    console.log('🎨 Applying materials from form...', formValues);

    // STEP 1: Hide ALL meshes first
    console.log('🙈 Step 1: Hiding all meshes...');
    obj.traverse((child) => {
      if (child.isMesh) {
        child.visible = false;
        console.log(`  ○ ${child.name} → hidden`);
      }
    });

    let materialCount = 0;

    // STEP 2: Apply materials and make visible where materialPicker matches
    console.log('🧱 Step 2: Applying materials where materialPicker matches...');

    // Collect all meshes and their material keys first
    const meshMaterialPairs = [];
    obj.traverse((child) => {
      if (child.isMesh) {
        // Ensure geometry is set up
        ensureGeometrySetup(child.geometry, child.name);

        // Find which materialPicker matches this mesh
        for (const config of formConfig) {
          if (config.type === 'materialPicker' && config.meshSelector) {
            // Check if mesh is excluded
            if (config.meshExcluder && isExcluded(child.name, config.meshExcluder)) {
              console.log(`  ⊘ ${child.name} → excluded by meshExcluder`);
              continue; // Skip this config
            }

            if (matchesMeshSelector(child.name, config.meshSelector)) {
              const selectedMaterialKey = formValues[config.name];

              if (!selectedMaterialKey) {
                console.warn(`⚠️ No material selected for ${config.name}, skipping ${child.name}`);
                continue;
              }

              meshMaterialPairs.push({ child, selectedMaterialKey, config });
              break; // Found a match, no need to check other configs
            }
          }
        }
      }
    });

    // Load all unique materials once (if not already cached by TexturePreloader)
    const uniqueMaterialKeys = [...new Set(meshMaterialPairs.map(p => p.selectedMaterialKey))];
    await Promise.all(uniqueMaterialKeys.map(key => loadMaterialTextures(key)));

    // Now apply materials to meshes (materials are already loaded and cached)
    for (const { child, selectedMaterialKey, config } of meshMaterialPairs) {
      // Get from cache (already loaded above or by TexturePreloader)
      const materialData = getCachedMaterial(selectedMaterialKey);
      const matConfig = materialConfig[selectedMaterialKey];

      if (materialData && matConfig) {
        // Create material only once per materialKey (cached in materialData)
        if (!materialData.material) {
          materialData.material = createMaterialFromConfig(materialData.textures, materialData.properties, false);
          console.log(`  🆕 Created new material instance for ${matConfig.name}`);
        }
        console.log(`  ✓ ${child.name} → ${matConfig.name} (visible with textures)`);
        child.material = materialData.material;

        // Debug: Log material properties
        if (child.material.map) {
          console.log(`    🖼️  Albedo texture: repeat=(${child.material.map.repeat.x}, ${child.material.map.repeat.y}), loaded=${!!child.material.map.image}`);
        }

        child.castShadow = true;
        child.receiveShadow = true;
        child.visible = true; // Make visible on match
        materialCount++;

        // Apply TextureManager for intelligent scaling and rotation (if enabled)
        if (USE_TEXTURE_MANAGER) {
          // Store reference to child for async texture manager application
          const meshToUpdate = child;
          const configToApply = matConfig;

          // Wait for texture to load, then apply TextureManager
          const checkAndApply = () => {
            if (meshToUpdate.material.map && meshToUpdate.material.map.image) {
              console.log(`\n🎯 Applying TextureManager to ${meshToUpdate.name} (${configToApply.type})...`);

              // Debug: Log mesh world matrix and parent scale
              meshToUpdate.updateWorldMatrix(true, false);
              const worldScale = new THREE.Vector3();
              meshToUpdate.getWorldScale(worldScale);
              console.log(`   🌍 World scale: ${worldScale.x.toFixed(4)}, ${worldScale.y.toFixed(4)}, ${worldScale.z.toFixed(4)}`);
              console.log(`   📦 Local scale: ${meshToUpdate.scale.x.toFixed(4)}, ${meshToUpdate.scale.y.toFixed(4)}, ${meshToUpdate.scale.z.toFixed(4)}`);

              textureManagerRef.current.applyToMesh(meshToUpdate, {
                type: configToApply.type || 'non-directional',
                unitsPerRepeat: configToApply.unitsPerRepeat || 500,
                materialConfig: configToApply,
                forceDirection: null, // Let TextureManager auto-detect
                rotationOffset: 0,
                squareThreshold: 1.2
              });
            } else {
              // Retry after a short delay
              setTimeout(checkAndApply, 50);
            }
          };

          setTimeout(checkAndApply, 100);
        }
      } else {
        console.error(`  ❌ Failed to load material data for ${selectedMaterialKey} on ${child.name}`);
      }
    }

    console.log(`🎨 Applied ${materialCount} materials and made visible`);
  };

  const loadObjModel = async (objString) => {
    const loader = new OBJLoader();
    const obj = loader.parse(objString);

    // First, remove ALL objects with userData.fromBackend flag to prevent duplicates
    const objectsToRemove = [];
    scene.traverse((child) => {
      if (child.userData?.fromBackend) {
        objectsToRemove.push(child);
      }
    });

    console.log(`🗑️ Removing ${objectsToRemove.length} backend objects from scene`);
    objectsToRemove.forEach((oldObj) => {
      scene.remove(oldObj);
      oldObj.traverse((child) => {
        if (child.isMesh) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose());
            } else {
              child.material.dispose();
            }
          }
        }
      });
    });

    obj.scale.set(0.01, 0.01, 0.01);
    obj.rotateX(-Math.PI / 2);
    obj.position.set(0, 0, 0);

    // Mark this object as coming from backend
    obj.userData.fromBackend = true;

    console.log('➕ Adding new object to scene');
    scene.add(obj);
    currentObjRef.current = obj;

    console.log('📊 Scene children count:', scene.children.length);
    console.log('📊 Object visible:', obj.visible);
    console.log('📊 Object position:', obj.position);
    console.log('📊 Object in scene:', scene.children.includes(obj));

    // Count meshes in object
    let meshCount = 0;
    obj.traverse((child) => {
      if (child.isMesh) meshCount++;
    });
    console.log('📊 Mesh count in object:', meshCount);

    // Apply materials from form config
    await applyMaterialsFromForm(obj);

    // Log all shapes and materials in the object
    console.log('🔍 Object structure:');
    const shapes = [];
    const materials = [];
    obj.traverse((child) => {
      if (child.isMesh) {
        shapes.push({
          name: child.name,
          type: child.geometry.type,
          vertices: child.geometry.attributes.position?.count || 0,
          faces: child.geometry.index ? child.geometry.index.count / 3 : 0
        });
        materials.push({
          meshName: child.name,
          materialName: child.material.name,
          materialType: child.material.type,
          color: child.material.color ? `#${child.material.color.getHexString()}` : 'none'
        });
      }
    });
    console.table(shapes);
    console.log('🎨 Materials:', materials);

    // Calculate bounding box and adjust camera/controls (only on first load)
    if (!hasInitializedCamera.current) {
      const box = new THREE.Box3().setFromObject(obj);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());

      // Get the max dimension of the object
      const maxDim = Math.max(size.x, size.y, size.z);

      // Calculate the distance needed to fit the object in view
      // Using both horizontal and vertical FOV to ensure object fits
      const fov = camera.fov * (Math.PI / 180);
      const aspect = camera.aspect;

      // Calculate distance based on the larger dimension (width or height)
      const fovVertical = fov;
      const fovHorizontal = 2 * Math.atan(Math.tan(fov / 2) * aspect);

      // Use the smaller FOV to ensure object fits
      const effectiveFov = Math.min(fovVertical, fovHorizontal);

      // Add margin factor for better framing (1.4 = 40% margin)
      const marginFactor = 1.5;
      let cameraDistance = (maxDim / 2) / Math.tan(effectiveFov / 2) * marginFactor;

      // Position camera at a low angle (only on first load)
      const cameraAngle = new THREE.Vector3(1.5, 0.05, 1.5).normalize();
      const cameraPosition = cameraAngle.multiplyScalar(cameraDistance).add(center);

      camera.position.copy(cameraPosition);
      camera.lookAt(center);
      camera.updateProjectionMatrix();

      // Update OrbitControls target and distance limits
      if (controls) {
        controls.target.copy(center);
        controls.minDistance = cameraDistance * 0.5;
        controls.maxDistance = cameraDistance * 2;
        controls.update();
      }

      console.log('📐 Object size:', size);
      console.log('📐 Max dimension:', maxDim);
      console.log('📐 Camera distance:', cameraDistance);
      console.log('📐 Camera position:', cameraPosition);
      console.log('📐 Camera target:', center);

      hasInitializedCamera.current = true;
    } else {
      // After first load: only update controls target and limits, NOT camera position
      const box = new THREE.Box3().setFromObject(obj);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);

      const fov = camera.fov * (Math.PI / 180);
      const aspect = camera.aspect;
      const fovVertical = fov;
      const fovHorizontal = 2 * Math.atan(Math.tan(fov / 2) * aspect);
      const effectiveFov = Math.min(fovVertical, fovHorizontal);
      const marginFactor = 1.5;
      let cameraDistance = (maxDim / 2) / Math.tan(effectiveFov / 2) * marginFactor;

      // Update OrbitControls target and distance limits
      if (controls) {
        controls.target.copy(center);
        controls.minDistance = cameraDistance * 0.5;
        controls.maxDistance = cameraDistance * 2;
        controls.update();
      }

      console.log('📐 Updated controls target (camera position unchanged)');
    }

    // Apply form colors after loading
    applyFormColors(obj);
  };

  // Note: Texture preloading is now handled by useTexturePreloader in ConfiguratorContainer
  // This ensures textures start loading immediately, independent of WebSocket connection

  // Load OBJ when objData changes
  useEffect(() => {
    if (objData) {
      console.log(`🔄 objData changed (ID: ${objData.requestId}), loading new model...`);
      isLoadingNewModelRef.current = true;

      // Extract the actual content from the wrapped objData
      loadObjModel(objData.content).finally(() => {
        // Reset flag after model loading completes
        setTimeout(() => {
          isLoadingNewModelRef.current = false;
          console.log('✅ Model loading complete, form updates will now be processed');
        }, 100);
      });
    }
  }, [objData]);

  // Update materials and colors when form values change
  // BUT: Skip if we're currently loading a new model (to prevent double render)
  useEffect(() => {
    if (currentObjRef.current && formValues && !isLoadingNewModelRef.current) {
      console.log('🎨 Form values changed, updating materials and colors...');
      (async () => {
        await applyMaterialsFromForm(currentObjRef.current);
        applyFormColors(currentObjRef.current);
      })();
    } else if (isLoadingNewModelRef.current) {
      console.log('⏭️ Skipping form values update (new model is loading)');
    }
  }, [formValues]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (currentObjRef.current) {
        scene.remove(currentObjRef.current);
        currentObjRef.current.traverse((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) child.material.dispose();
        });
      }
      // Clear texture manager
      textureManagerRef.current.clear();
    };
  }, []);

  return null;
};
